import Button from '@mui/material/Button';
import queryString from 'query-string';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import API from '../../../api';
import { SnackbarContext, UserContext } from '../../../contexts';
import { buttonStyle } from '../../../styles/style';
import { creditFormInitialValues } from './form';
import { IssueForm, MoveForm, OffsetRetireForm } from './forms';
import { useCreditFormValidations, useReasonMenus } from './hooks/useCreditForm';

export const CreditForm = ({ type, credit, onSubmit }) => {
  const [reductionOrCreditList, setReductionOrCreditList] = useState([]);
  const history = useHistory();
  const searchObject = queryString.parse(history.location.search);
  const { t } = useTranslation();
  const { updateSnackbar } = useContext(SnackbarContext);
  const { userInfo } = useContext(UserContext);
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: creditFormInitialValues[type] || {},
  });

  useEffect(() => {
    API.Credit.GetProject({
      mode: type.charAt(0).toUpperCase() + type.slice(1),
    })
      .then((res) => {
        const list = type !== 'issue' ? res.data.filter((item) => item.amount > 0) : res.data;
        setReductionOrCreditList(list);
      })
      .catch((err) => {
        updateSnackbar({
          type: 'error',
          open: true,
          message: err.data.message,
        });
      });
  }, [type, updateSnackbar]);

  /* 발행가능한 프로젝트 목록 및 이전, 상쇄, 철회 가능한 프로젝트 목록 */
  const selectMenus = useMemo(() => {
    return reductionOrCreditList.map((item) => {
      return {
        // issue 일 경우 감축량 seq, 외에는 credit seq
        value: type === 'issue' ? item.reductionCert.seq : item.seq,
        label:
          type === 'issue'
            ? `${item.title} (No: ${item.reductionCert.seq}, ${item.reductionCert.monitorDegree}D)`
            : item.title,
      };
    });
  }, [reductionOrCreditList, type]);

  /* 선택된 감축량 또는 프로젝트 */
  // 신규 발행(issue)일 경우 ReductionCertSeq, 외에는 ProjectSeq
  const reductionCertSeq = watch('reductionCertSeq');
  const projectSeq = watch('projectSeq');

  const selectedMenu = useMemo(() => {
    const selectedSeq = type === 'issue' ? reductionCertSeq : projectSeq;
    return reductionOrCreditList.find((item) =>
      type === 'issue' ? item.reductionCert.seq === selectedSeq : item.seq === selectedSeq,
    );
  }, [reductionOrCreditList, type, reductionCertSeq, projectSeq]);

  /* Validation rules */
  const validations = useCreditFormValidations(type);

  /* 사유 목록 */
  const reasonMenus = useReasonMenus();

  useEffect(() => {
    if (credit) {
      if (type === 'move') {
        setValue('assignorName', credit.assignorName);
        setValue('assignorPhone', credit.assignorPhone);
        setValue('assignorEmail', credit.assignorEmail);
        setValue('assignorTeam', credit.assignorTeam);
        setValue('assignorUserSeq', credit.assignorUserSeq);
        setValue('assignorUserName', userInfo?.ceoName);
        setValue('assignorUserCompanyName', userInfo?.companyName);
        setValue('assigneeName', credit.assigneeName);
        setValue('assigneePhone', credit.assigneeUser?.phone);
        setValue('assigneeEmail', credit.assigneeEmail);
        setValue('assigneeTeam', credit.assigneeTeam);
        setValue('assigneeUserSeq', credit.assigneeUserSeq);
        setValue('assigneeUserCompanyName', credit.assigneeUser?.companyName);
        setValue('projectSeq', credit?.zcCredit?.project?.seq);
        setValue('amount', credit.amount);
        setValue('reason', credit.reason);
        setValue('reasonDetail', credit.reasonDetail);
        setValue('files', credit.moveFiles);
      } else {
        if (type !== 'issue') {
          setValue('projectSeq', credit?.zcCredit?.project?.seq);
        }
        setValue('name', credit.assignorName);
        setValue('phone', credit.assignorPhone);
        setValue('email', credit.assignorEmail);
        setValue('team', credit.assignorTeam);
        setValue('amount', credit.amount);
        setValue('reason', credit.reason);
        setValue('reasonDetail', credit.reasonCustom);
        setValue('files', credit.moveFiles);
      }
    }
  }, [credit, userInfo]);

  const renderForm = () => {
    switch (type) {
      case 'issue':
        return (
          <IssueForm
            control={control}
            errors={errors}
            register={register}
            selectMenus={selectMenus}
            validations={validations}
          />
        );
      case 'move':
        return (
          <MoveForm
            control={control}
            errors={errors}
            register={register}
            selectMenus={selectMenus}
            selectedMenu={selectedMenu}
            reasonMenus={reasonMenus}
            watch={watch}
            validations={validations}
            setValue={setValue}
          />
        );
      case 'offset':
      case 'retire':
        return (
          <OffsetRetireForm
            control={control}
            errors={errors}
            register={register}
            watch={watch}
            reasonMenus={reasonMenus}
            selectMenus={selectMenus}
            selectedMenu={selectedMenu}
            type={type}
            validations={validations}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <form noValidate onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4'>
        {renderForm()}
        <section className='button__container' style={{}}>
          <Button
            sx={{ ...buttonStyle, width: '240px' }}
            disableElevation
            type='submit'
            variant='contained'
            color='primary'
          >
            {searchObject.seq ? t('COMPLEMENT_APPLY') : t('DO_APPLY')}
          </Button>
        </section>
      </form>
    </div>
  );
};
